import * as React from "react";
import SoftTypography from "components/SoftTypography";
import SoftBox from "components/SoftBox";
import { Card, Grid, Stack } from "@mui/material";
import KendoButton from "assets/theme/components/button/kendo";
import KendoInput from "assets/theme/components/inputKendo";
import FormField from "pages/components/FormField";
import { useFormik } from "formik";
import * as Yup from "yup";
import toast from "react-hot-toast";
import { OrganizationCompanyApiService } from "api/organizations.api";
import CommonApiService from "api/common.api";
import { Link, useLocation, useNavigate } from "react-router-dom";
import DashboardLayout from "pages/components/LayoutContainers/DashboardLayout";
import DashboardNavbar from "pages/DashboardNavbar";

const EditCompanyPage = ({ selectedItem }) => {
  const [loading, setLoading] = React.useState(false);
  const [refresh, setRefresh] = React.useState(false);
  const [countries, setCountries] = React.useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const companyId = queryParams.get("c");

  const [initialValues, setInitialValues] = React.useState({
    name: "",
    firstName: "",
    lastName: "",
    phone: "",
    addressLineOne: "",
    addressLineTwo: "",
    city: "",
    state: "",
    country: "",
    zipCode: "",
    description: "",
  });

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: initialValues,
    validationSchema: Yup.object({
      name: Yup.string().required("Company name is required"),
      firstName: Yup.string().optional(),
      lastName: Yup.string().optional(),
      phone: Yup.string()
        .matches(/^\+?[1-9]\d{1,14}$/, "Invalid phone number")
        .required("Contact number is required"),
      addressLineOne: Yup.string().required("This field is required"),
      addressLineTwo: Yup.string().required("This field is required"),
      city: Yup.string().required("This field is required"),
      state: Yup.string().required("This field is required"),
      country: Yup.string().required("This field is required"),
      zipCode: Yup.string().required("This field is required"),
      description: Yup.string().max(255, "Enter maximum 255 characters"),
    }),
    onSubmit: (values) => {
      saveData({
        id: values.id,
        name: values.name,
        firstName: values.firstName,
        lastName: values.lastName,
        phone: values.phone,
        addressLineOne: values.addressLineOne,
        addressLineTwo: values.addressLineTwo,
        city: values.city,
        state: values.state,
        country: values.country,
        zipCode: values.zipCode,
        description: values.description,
      });
    },
  });

  const getCompanyDetails = async () => {
    try {
      setLoading(true);
      const response = await OrganizationCompanyApiService.get(companyId);
      const data = response?.data?.data;

      setInitialValues({
        id: data.id || "",
        name: data.name || "",
        firstName: data.firstName || "",
        lastName: data.lastName || "",
        phone: data.phone || "",
        addressLineOne: data.addressLineOne || "",
        addressLineTwo: data.addressLineTwo || "",
        city: data.city || "",
        state: data.state || "",
        country: data.country || "",
        zipCode: data.zipCode || "",
        description: data.description || "",
      });
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const saveData = async (formData) => {
    try {
      setLoading(true);
      const response = await OrganizationCompanyApiService.update(formData);
      console.log(response);
      toast.success("Company updated successfully");
      setRefresh(!refresh);
      formik.resetForm();
      setTimeout(() => {
        navigate("/companies");
      }, 800);
    } catch (error) {
      console.error(error);
      if (error?.response?.data?.message) {
        toast.error(error?.response?.data?.message);
      } else {
        toast.error(error?.message || "Something went wrong");
      }
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    if (selectedItem) {
      setInitialValues({
        id: selectedItem.id || "",
        name: selectedItem.name || "",
        firstName: selectedItem.firstName || "",
        lastName: selectedItem.lastName || "",
        phone: selectedItem.phone || "",
        addressLineOne: selectedItem.addressLineOne || "",
        addressLineTwo: selectedItem.addressLineTwo || "",
        city: selectedItem.city || "",
        state: selectedItem.state || "",
        country: selectedItem.country || "",
        zipCode: selectedItem.zipCode || "",
        description: selectedItem.description || "",
      });
    }
  }, [selectedItem]);

  const getCountries = async () => {
    try {
      setLoading(true);
      const response = await CommonApiService.getCountries();
      setCountries(response?.data?.data);
    } catch (error) {
      console.error(error);
      if (!!error?.response?.data?.message) {
        toast.error(error?.response?.data?.message);
      } else {
        toast.error(error?.message || "Something went wrong");
      }
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    getCountries();
  }, []);

  React.useEffect(() => {
    getCompanyDetails();
  }, [companyId, refresh]);

  console.log("selected Id===========>", companyId);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Link to={""}>
        <SoftTypography variant="h5" fontSize="medium" mb={2} onClick={() => navigate(-1)}>
          Go Back
        </SoftTypography>
      </Link>

      <Grid container spacing={2}>
        <Grid item lg={7} md={12}>
          <Card sx={{ padding: 4 }}>
            <Stack
              direction={{ xs: "column", md: "row" }}
              justifyContent="space-between"
              alignItems="flex-start"
            >
              <SoftTypography variant="h4">Edit Company</SoftTypography>
            </Stack>
            <SoftBox component="form" role="form" onSubmit={formik.handleSubmit}>
              <Grid container spacing={1} mt={2}>
                <Grid item xs={12} lg={12} mt={1}>
                  <SoftTypography
                    fontSize="small"
                    fontWeight="bold"
                    letterSpacing={0.3}
                    variant="h5"
                    mb={0.5}
                  >
                    Company Name
                  </SoftTypography>
                  <KendoInput
                    type="text"
                    placeholder="Enter company name"
                    name="name"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.name}
                  />
                  {formik.touched.name && formik.errors.name ? (
                    <div className="form-error error">{formik.errors.name}</div>
                  ) : null}
                </Grid>
                <Grid item xs={12} lg={12} mt={1}>
                  <SoftTypography
                    fontSize="small"
                    fontWeight="bold"
                    letterSpacing={0.3}
                    variant="h5"
                    mb={0.5}
                  >
                    First name
                  </SoftTypography>
                  <KendoInput
                    type="tel"
                    placeholder="Enter first name"
                    name="firstName"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.firstName}
                  />
                  {formik.touched.firstName && formik.errors.firstName ? (
                    <div className="form-error error">{formik.errors.firstName}</div>
                  ) : null}
                </Grid>
                <Grid item xs={12} lg={12} mt={1}>
                  <SoftTypography
                    fontSize="small"
                    fontWeight="bold"
                    letterSpacing={0.3}
                    variant="h5"
                    mb={0.5}
                  >
                    Last name
                  </SoftTypography>
                  <KendoInput
                    type="tel"
                    placeholder="Enter last name"
                    name="lastName"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.lastName}
                  />
                  {formik.touched.lastName && formik.errors.lastName ? (
                    <div className="form-error error">{formik.errors.lastName}</div>
                  ) : null}
                </Grid>
                <Grid item xs={12} lg={12} mt={1}>
                  <SoftTypography
                    fontSize="small"
                    fontWeight="bold"
                    letterSpacing={0.3}
                    variant="h5"
                    mb={0.5}
                  >
                    Contact Number
                  </SoftTypography>
                  <KendoInput
                    type="tel"
                    placeholder="Enter contact number"
                    name="phone"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.phone}
                  />
                  {formik.touched.phone && formik.errors.phone ? (
                    <div className="form-error error">{formik.errors.phone}</div>
                  ) : null}
                </Grid>
                <Grid item xs={12} lg={12} mt={1}>
                  <SoftTypography
                    fontSize="small"
                    fontWeight="bold"
                    letterSpacing={0.3}
                    variant="h5"
                    mb={0.5}
                  >
                    Address
                  </SoftTypography>
                  <KendoInput
                    placeholder="Enter address line 1"
                    name="addressLineOne"
                    maxLength="100"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.addressLineOne}
                  />
                  {formik.touched.addressLineOne && formik.errors.addressLineOne ? (
                    <div className="form-error error">{formik.errors.addressLineOne}</div>
                  ) : null}
                </Grid>
                <Grid item xs={12} lg={12} mt={1}>
                  <KendoInput
                    placeholder="Enter address line 2"
                    name="addressLineTwo"
                    maxLength="100"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.addressLineTwo}
                  />
                  {formik.touched.addressLineTwo && formik.errors.addressLineTwo ? (
                    <div className="form-error error">{formik.errors.addressLineTwo}</div>
                  ) : null}
                </Grid>
                <Grid item xs={12} lg={12} mt={1}>
                  <KendoInput
                    placeholder="Enter City"
                    name="city"
                    maxLength="100"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.city}
                  />
                  {formik.touched.city && formik.errors.city ? (
                    <div className="form-error error">{formik.errors.city}</div>
                  ) : null}
                </Grid>
                <Grid item xs={12} lg={12} mt={1}>
                  <KendoInput
                    placeholder="Enter State"
                    name="state"
                    maxLength="100"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.state}
                  />
                  {formik.touched.state && formik.errors.state ? (
                    <div className="form-error error">{formik.errors.state}</div>
                  ) : null}
                </Grid>
                <Grid item xs={12} lg={12} mt={1}>
                  <select
                    name="country"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.country}
                    className="custom-select"
                  >
                    <option value={""}> Select Country </option>
                    {countries?.map((option) => (
                      <option key={option.id} value={option.nicename}>
                        {option.nicename}
                      </option>
                    ))}
                  </select>
                  {formik.touched.country && formik.errors.country ? (
                    <div className="form-error error">{formik.errors.country}</div>
                  ) : null}
                </Grid>
                <Grid item xs={12} lg={12} mt={1}>
                  <KendoInput
                    placeholder="Enter zip code"
                    name="zipCode"
                    maxLength="10"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.zipCode}
                  />
                  {formik.touched.zipCode && formik.errors.zipCode ? (
                    <div className="form-error error">{formik.errors.zipCode}</div>
                  ) : null}
                </Grid>
                <Grid item xs={12} lg={12} mt={1}>
                  <FormField
                    placeholder="Enter description"
                    name="description"
                    label="Description"
                    value={formik.values.description}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    multiline
                    rows={2}
                  />
                  {formik.touched.description && formik.errors.description ? (
                    <div className="form-error error">{formik.errors.description}</div>
                  ) : null}
                </Grid>
                <Grid item mt={3}>
                  <KendoButton type="submit" label="Submit" loading={loading} />
                </Grid>
              </Grid>
            </SoftBox>
          </Card>
        </Grid>
      </Grid>
    </DashboardLayout>
  );
};

export default EditCompanyPage;
